import WarningIcon from "@mui/icons-material/Warning";
import {
  Box,
  Button,
  Chip,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import lodash, { cloneDeep } from "lodash";
import { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { saveTestDataFile } from "src/clients/TestDataService";
import { selectUser } from "src/redux/slices/userSlice";
import { TestDataFeatureDto } from "src/types/TestDataFeatureDto";
import { TOAST_MESSAGE } from "../../constants/Constants";
import { useTestDataContext } from "../../context/Context";
import WarningPopup from "../../modal/WarningPopup";
import NotifyError from "../../notifications/NotifyError";
import NotifySuccess from "../../notifications/NotifySuccess";
import editTestDataStyles from "./EditTestData.module.css";
import EditTestDataComponent from "./EditTestDataComponent";

interface EditTestDataProps {
  isNewTestDataFile: boolean;
  testDataFileName: string;
  entityType: string;
  mappedFeatureList: string[];
  selectedOrgCode: string;
  entityTypeList: string[];
  userFeatureNameList: string[];
  selectedTestData: any;
  handleOnEditBack: () => void;
  handleOnSaveBack: () => Promise<void>;
}
const EditTestData = ({
  isNewTestDataFile,
  testDataFileName,
  entityType,
  mappedFeatureList,
  selectedOrgCode,
  entityTypeList,
  userFeatureNameList,
  selectedTestData,
  handleOnEditBack,
  handleOnSaveBack,
}: EditTestDataProps) => {
  const [editedTestData, setEditedTestData] = useState({ ...selectedTestData });
  const [editedTestDataFileName, setEditedTestDataFileName] =
    useState(testDataFileName);
  const [selectedEntityType, setEntityType] = useState<string>(entityType);
  const [selectedFeatureList, setSelectedFeatureList] =
    useState<string[]>(mappedFeatureList);
  const [backToTestDataLandingPage, setBackToTestDataLandingPage] =
    useState<boolean>(false);
  const [isExpandAll, setIsExpandAll] = useState<boolean>(false);
  const [expandCollapseKey, setExpandCollapseKey] = useState(0);
  const { initialTestData } = useTestDataContext();
  const user = useSelector(selectUser);

  const getAllUserTestDataNames = () => {
    return Array.from(
      initialTestData?.get(user.userId)?.get(selectedOrgCode) ||
        new Map<string, any>()
    ).map(([key]) => key);
  };

  const isFileNameUnique = !getAllUserTestDataNames().some(
    (key) =>
      key.replace(".json", "") === editedTestDataFileName &&
      testDataFileName !== editedTestDataFileName
  );

  const handleMainChange = (editedData: any) => {
    setEditedTestData(cloneDeep(editedData));
  };

  const handleMappedFeatureList = (value: string) => {
    if (selectedFeatureList.includes(value)) {
      setSelectedFeatureList((prev) => prev.filter((item) => item !== value));
    } else {
      setSelectedFeatureList((prev) => [...prev, value]);
    }
  };

  const toggleExpandCollapse = (isExpand: boolean) => {
    setIsExpandAll(isExpand);
    setExpandCollapseKey((prevKey) => prevKey + 1);
  };

  const handleEntityChange = (event: any) => {
    setEntityType(event.target.value);
  };

  const handleOnCloseIcon = () => {
    setBackToTestDataLandingPage(false);
  };

  const handleOnSave = async () => {
    setBackToTestDataLandingPage(false);
    const testDataFeatureDTO: TestDataFeatureDto = {
      clientId: user.clientId,
      entityTypeName: selectedEntityType,
      featureFilesList: selectedFeatureList,
      organization: selectedOrgCode,
      testDataFileName: editedTestDataFileName,
      previousTestDataFileName: testDataFileName,
      userDetailsId: user.userDetailsId,
    };
    const response = await saveTestDataFile(
      editedTestData,
      testDataFeatureDTO,
      user.auth,
      editedTestDataFileName,
      selectedOrgCode,
      user.productCode,
      user.selectedClient
    );
    if (response.ok) {
      await handleOnSaveBack();
      toast.success(
        <NotifySuccess header={"Success"}>
          <Typography variant="body2">
            {editedTestDataFileName} file saved successfully
          </Typography>
        </NotifySuccess>,
        {
          icon: false,
          closeButton: false,
        }
      );
    } else if (response.status === 408) {
      toast.error(
        <NotifyError header={"Error"}>
          <Typography variant="body2">
            {TOAST_MESSAGE.FAILED_TO_CONNECT}
          </Typography>
        </NotifyError>,
        {
          icon: false,
          closeButton: false,
        }
      );
    } else {
      toast.error(
        <NotifyError header={"Error"}>
          <Typography variant="body2">
            {TOAST_MESSAGE.INTERNAL_SERVER_ERROR}
          </Typography>
        </NotifyError>,
        {
          icon: false,
          closeButton: false,
        }
      );
    }
  };

  const handleOnBack = () => {
    setBackToTestDataLandingPage(true);
    if (
      JSON.stringify(selectedTestData) === JSON.stringify(editedTestData) &&
      testDataFileName === editedTestDataFileName &&
      entityType === selectedEntityType &&
      lodash.isEqual(mappedFeatureList.sort(), selectedFeatureList.sort())
    ) {
      handleOnEditBack();
    }
  };

  const checkForEmptyValues = (obj: any): boolean => {
    // Iterates over object, checks if any nested key or value is empty.
    let isEmptyFound = false;

    // Recursive function to walk through the object
    const recursiveCheck = (currentObj: any) => {
      lodash.forOwn(currentObj, (value, key) => {
        if (key === "" || value === "") {
          isEmptyFound = true; // Check if either key or value is empty string
        }

        // If the value is an empty object, flag it as invalid
        if (lodash.isObject(value) && lodash.isEmpty(value)) {
          isEmptyFound = true; // Flag if the value is an empty object
        }

        if (lodash.isObject(value)) {
          recursiveCheck(value); // Recurse if it's a nested object
        }
      });
    };

    // Start the recursive check
    recursiveCheck(obj);
    return isEmptyFound;
  };

  return (
    <>
      <Box>
        <Box className={editTestDataStyles.headerOutterBox}>
          <Box>
            <TextField
              variant="standard"
              value={editedTestDataFileName}
              label={
                editedTestDataFileName === "" ? "Enter Test Data File Name" : ""
              }
              helperText={
                isFileNameUnique ? "" : "Test Data File already exists"
              }
              size="small"
              inputProps={{
                style: {
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  color: "#444343",
                  width: "24rem",
                },
              }}
              InputLabelProps={{
                shrink: false,
              }}
              sx={{
                "& .MuiInputBase-root.MuiInput-root": {
                  marginTop: "8px",
                },
              }}
              onChange={(e) => {
                setEditedTestDataFileName(e.target.value);
              }}
            />
            <Box className={editTestDataStyles.headerInnerChildBox}>
              <Typography variant="body2">Entity Type</Typography>
              <FormControl>
                <Select
                  sx={{ width: "15rem" }}
                  size="small"
                  value={selectedEntityType}
                  onChange={handleEntityChange}
                  disabled={!isNewTestDataFile}
                >
                  {entityTypeList.map((entityType) => (
                    <MenuItem
                      key={entityType}
                      value={entityType}
                    >
                      <ListItemText primary={entityType} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box className={editTestDataStyles.headerInnerChildBox}>
            <Typography
              variant="body2"
              sx={{ whiteSpace: "nowrap" }}
            >
              Feature File Map
            </Typography>
            <FormControl>
              <Select
                sx={{ width: "30rem" }}
                displayEmpty
                size="small"
                multiple
                value={selectedFeatureList}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 40 * 10,
                      width: 220,
                      overflowX: "auto",
                    },
                  },
                }}
                renderValue={(selectedValues) => (
                  <Box
                    sx={{
                      display: "flex",
                      maxHeight: "4rem",
                      overflow: "scroll",
                      flexWrap: "wrap",
                      gap: 0.5,
                    }}
                  >
                    {selectedValues.length !== 0 ? (
                      selectedValues.map((value: string) => (
                        <Chip
                          key={value}
                          label={value}
                          onMouseDown={(e) => e.stopPropagation()}
                          onDelete={() => {
                            handleMappedFeatureList(value);
                          }}
                        />
                      ))
                    ) : (
                      <span style={{ color: "#aaa" }}> Feature Files</span>
                    )}
                  </Box>
                )}
              >
                {userFeatureNameList?.map((value: string) => (
                  <MenuItem
                    key={value}
                    value={value}
                    onClick={() => handleMappedFeatureList(value)}
                  >
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box className={editTestDataStyles.expandAllBox}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => toggleExpandCollapse(true)}
          >
            Expand All
          </Button>

          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => toggleExpandCollapse(false)}
          >
            Collapse All
          </Button>
        </Box>

        <Box className={editTestDataStyles.mainBox}>
          <EditTestDataComponent
            isExpandAll={isExpandAll}
            expandCollapseKey={expandCollapseKey}
            innerJson={editedTestData}
            handleInnerJsonChange={(editedData: any) => {
              handleMainChange(cloneDeep(editedData));
            }}
          />
        </Box>
        {/* action buttons */}
        <Box className={editTestDataStyles.actionButtonsBox}>
          <Button
            variant="outlined"
            disabled={
              (!isNewTestDataFile &&
                JSON.stringify(selectedTestData) ===
                  JSON.stringify(editedTestData) &&
                (testDataFileName === editedTestDataFileName ||
                  editedTestDataFileName === "" ||
                  !isFileNameUnique) &&
                entityType === selectedEntityType &&
                lodash.isEqual(
                  mappedFeatureList.sort(),
                  selectedFeatureList.sort()
                )) ||
              lodash.isEmpty(editedTestData) ||
              checkForEmptyValues(editedTestData)
            }
            onClick={() => {
              handleOnSave();
            }}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            onClick={handleOnBack}
          >
            Back
          </Button>
        </Box>
      </Box>

      {backToTestDataLandingPage &&
        (JSON.stringify(selectedTestData) !== JSON.stringify(editedTestData) ||
          testDataFileName !== editedTestDataFileName ||
          entityType === selectedEntityType ||
          lodash.isEqual(
            mappedFeatureList.sort(),
            selectedFeatureList.sort()
          )) && (
          <WarningPopup
            isOpen={true}
            onClose={handleOnCloseIcon}
            actionButtons={[
              {
                name: "Save",
                isDisabled:
                  (!isNewTestDataFile &&
                    JSON.stringify(selectedTestData) ===
                      JSON.stringify(editedTestData) &&
                    (testDataFileName === editedTestDataFileName ||
                      editedTestDataFileName === "" ||
                      !isFileNameUnique) &&
                    entityType === selectedEntityType &&
                    lodash.isEqual(
                      mappedFeatureList.sort(),
                      selectedFeatureList.sort()
                    )) ||
                  lodash.isEmpty(editedTestData) ||
                  checkForEmptyValues(editedTestData),

                onButtonClick: handleOnSave,
              },
              {
                name: "Discard",
                onButtonClick: handleOnEditBack,
              },
            ]}
          >
            <Box className={editTestDataStyles.warningPopUpBox}>
              <WarningIcon />
              <Typography variant="body2">
                There are changes in the data do you want to save it.
              </Typography>
            </Box>
          </WarningPopup>
        )}
    </>
  );
};

export default EditTestData;
